import { useState } from "react";
import { Clock, Edit, Eye, Search, Users, Plus } from "lucide-react";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { GenericTable } from "@shared/components/GenericTable";
import { useNumberFormats } from "../../../hooks";
import { NumberFormat } from "../../../types"; // Import the correct type

export type IdConfig = {
  id: string;
  name: string;
  type: "master" | "transaction";
  entity: string;
  format: string;
  lastModified: string;
  lastModifiedBy?: string;
  isActive: boolean;
  utilityService: string;
  prefix: string;
  currentNumber: number;
};

interface IdTableProps {
  onEdit: (id: string, rawData?: NumberFormat) => void;
  onViewLogs: (id: string) => void;
  onViewDetails: (id: string, rawData?: NumberFormat) => void;
  activeTab?: "all" | "master" | "transaction";
  onTabChange?: (tab: "all" | "master" | "transaction") => void;
  onCreateNew: () => void;
}

const IdTable = ({
  onEdit,
  onViewLogs,
  onViewDetails,
  activeTab = "all", // Default value
  onTabChange,
  onCreateNew,
}: IdTableProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Get data from API based on active tab
  const { data: numberFormatResponse, isLoading } = useNumberFormats(
    activeTab,
    currentPage,
    itemsPerPage,
  );

  // Extract the result array from the API response
  const numberFormatData: NumberFormat[] = numberFormatResponse?.result || [];

  // Transform API data to match table format
  const transformedData: IdConfig[] = numberFormatData.map(
    (item: NumberFormat) => ({
      id: item.id.toString(),
      name: `${item.submodule}`,
      type: item.configurationType == 1 ? "transaction" : "master",
      idType: item.configurationTypeDisplay,

      entity: item.submodule,
      format: item.formatPreview || "N/A",
      lastModified: item.lastModifiedDate || "N/A",
      lastModifiedBy: item.lastModifiedRemoteUser || "N/A",
      isActive: item.isActive,
      utilityService: item.utilityService || "N/A",
      prefix: item.prefix || "",
      currentNumber: item.currentno || 0,
    }),
  );

  // Filter data based on search term (client-side search)
  const filteredData = transformedData.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.entity.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.format.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.prefix.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleTabChange = (tab: "all" | "master" | "transaction") => {
    if (onTabChange) {
      onTabChange(tab); // Call parent's tab change handler
    }
    setSelectedIds([]); // Clear selection when changing tabs
    setCurrentPage(1); // Reset page on tab change
  };
  // Debug: Add console logs to check if actions are being called
  const handleViewDetails = (row: IdConfig) => {
    console.log("View Details clicked for:", row);
    const rawData = numberFormatData.find(
      (item) => item.id.toString() === row.id,
    );
    onViewDetails(row.id, rawData);
  };

  const handleViewLogs = (row: IdConfig) => {
    console.log("View Logs clicked for:", row);
    onViewLogs(row.id);
  };

  const handleEdit = (row: IdConfig) => {
    console.log("Edit clicked for:", row);
    const rawData = numberFormatData.find(
      (item) => item.id.toString() === row.id,
    );
    onEdit(row.id, rawData);
  };

  // Table columns configuration
  const tableColumns = [
    {
      key: "name",
      header: "ID Name",
      render: (value: string, row: IdConfig) => (
        <div>
          <span className="font-medium text-gray-900">{value}</span>
          {row.prefix && (
            <div className="text-xs text-gray-500">Prefix: {row.prefix}</div>
          )}
        </div>
      ),
    },
    {
      key: "idType",
      header: "ID Type",
      render: (value: string) => <span>{value}</span>,
    },
    {
      key: "entity",
      header: "Entity",
      render: (value: string) => <span className="text-gray-900">{value}</span>,
    },
    {
      key: "format",
      header: "Format Preview",
      render: (value: string) => (
        <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
          {value}
        </code>
      ),
    },
    {
      key: "currentNumber",
      header: "Current Number",
      render: (value: number) => (
        <span className="text-gray-700 text-sm font-mono">{value}</span>
      ),
    },
    {
      key: "lastModified",
      header: "Last Modified",
      render: (value: string) => (
        <span className="text-gray-700 text-sm">{value}</span>
      ),
    },
    {
      key: "lastModifiedBy",
      header: "Modified By",
      render: (value: string) => (
        <span className="text-gray-700 text-sm">{value || "-"}</span>
      ),
    },
    {
      key: "isActive",
      header: "Status",
      render: (value: boolean) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            value ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}
        >
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  // Table actions configuration
  const tableActions = [
    {
      label: "View Details",
      onClick: handleViewDetails,
      icon: <Eye className="h-4 w-4" />,
      variant: "ghost" as const,
    },
    {
      label: "Edit",
      onClick: handleEdit,
      icon: <Edit className="h-4 w-4" />,
      variant: "ghost" as const,
    },
  ];

  // Bulk actions
  const bulkActions = [
    {
      label: "Bulk Delete",
      onClick: (selectedData: IdConfig[], selectedIds: string[]) => {
        console.log("Bulk delete:", selectedIds);
        alert(`Bulk deleting ${selectedIds.length} items`);
      },
      icon: <Users className="h-4 w-4" />,
      variant: "outline" as const,
    },
  ];

  // Extract total items from API response
  const totalItems = numberFormatResponse?.count || 0;

  // Pagination configuration
  const paginationConfig = {
    currentPage,
    totalPages: Math.ceil(totalItems / itemsPerPage) || 1,
    totalItems,
    itemsPerPage,
    startItem: totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1,
    endItem: Math.min(currentPage * itemsPerPage, totalItems),
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelectionChange = (selectedIds: string[]) => {
    console.log("Selection changed:", selectedIds);
    setSelectedIds(selectedIds);
  };

  return (
    <div className="w-full space-y-4">
      {/* Header with search and filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1">
          <div className="relative flex-1 w-full sm:max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search ID configurations..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Tab filters */}
          <div className="inline-flex items-center rounded-md border border-input overflow-hidden">
            <button
              className={`px-4 py-2 text-sm ${
                activeTab === "all" ? "bg-primary text-white" : "hover:bg-muted"
              }`}
              onClick={() => handleTabChange("all")}
            >
              All
            </button>
            <button
              className={`px-4 py-2 text-sm border-l ${
                activeTab === "master"
                  ? "bg-primary text-white"
                  : "hover:bg-muted"
              }`}
              onClick={() => handleTabChange("master")}
            >
              Master
            </button>
            <button
              className={`px-4 py-2 text-sm border-l ${
                activeTab === "transaction"
                  ? "bg-primary text-white"
                  : "hover:bg-muted"
              }`}
              onClick={() => handleTabChange("transaction")}
            >
              Transaction
            </button>
          </div>
        </div>
      </div>

      {/* Results summary */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">
          ID Configurations ({totalItems})
          {selectedIds.length > 0 && ` - ${selectedIds.length} selected`}
        </h3>
      </div>

      {/* Generic Table */}
      <GenericTable
        data={filteredData}
        columns={tableColumns}
        rowKey="id"
        selectedRows={selectedIds}
        onSelectionChange={handleSelectionChange}
        actions={tableActions}
        actionsType="dropdown"
        bulkActions={bulkActions}
        pagination={paginationConfig}
        onPageChange={handlePageChange}
        emptyMessage="No ID configurations found matching your criteria"
        loading={isLoading}
      />
    </div>
  );
};

export default IdTable;
